import type { Participant } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { DateTime } from '@piccolohealth/util';
import React from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { ParticipantEditModal } from '../participant/ParticipantEditModal';
import { ParticipantProfileCardFieldGroup } from './ParticipantProfileCardFieldGroup';
import { ParticipantProfileCardGeneral } from './ParticipantProfileCardGeneral';
import { ParticipantProfileCardKeyContacts } from './ParticipantProfileCardKeyContacts';

export interface Props {
  participant: Participant;
  isDisabled: boolean;
}

export const ParticipantProfileCard = (props: Props) => {
  const { participant, isDisabled } = props;

  const [tabValue, setTabValue] = React.useState<string>('general');

  const onEditProfile = React.useCallback(() => {
    return showModal(ParticipantEditModal, {
      participantId: participant.id,
      isDisabled,
      openToTabValue: tabValue,
    });
  }, [isDisabled, participant.id, tabValue]);

  const dob = DateTime.fromISO(participant.dob.toString()).toLocaleString(DateTime.DATE_SHORT);
  const age = DateTime.now().diff(DateTime.fromISO(participant.dob.toString()), [
    'years',
    'months',
  ]).years;

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.HStack align='stretch'>
          <ChakraV3.Stack gap={0}>
            <ChakraV3.Heading size='xl' fontWeight='bold'>
              Profile
            </ChakraV3.Heading>
            <ChakraV3.Text fontSize='sm' color='secondary'>
              View and edit personal details
            </ChakraV3.Text>
          </ChakraV3.Stack>
          <ChakraV3.Spacer />
          <ChakraV3.Button onClick={onEditProfile} size='sm' variant='subtle'>
            <FaPencilAlt />
            Edit profile
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={4} h='full'>
          <ChakraV3.Tabs.Root
            defaultValue={tabValue}
            orientation='vertical'
            variant='subtle'
            onValueChange={({ value }) => setTabValue(value)}
          >
            <ChakraV3.Tabs.List>
              <ChakraV3.Tabs.Trigger value='general'>General</ChakraV3.Tabs.Trigger>
              <ChakraV3.Tabs.Trigger value='keyContacts'>Key contacts</ChakraV3.Tabs.Trigger>
              {participant.fieldGroups.map((fieldGroup) => (
                <ChakraV3.Tabs.Trigger
                  value={`${fieldGroup.template.name}-${fieldGroup.template.index}`}
                  key={fieldGroup.id}
                >
                  {fieldGroup.template.name}
                </ChakraV3.Tabs.Trigger>
              ))}
            </ChakraV3.Tabs.List>
            <ChakraV3.Tabs.ContentGroup
              w='full'
              ml={6}
              pl={3}
              borderLeftWidth='1px'
              borderLeftColor='gray.200'
            >
              <ChakraV3.Tabs.Content value='general'>
                <ParticipantProfileCardGeneral participant={participant} dob={dob} age={age} />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content value='keyContacts'>
                <ParticipantProfileCardKeyContacts keyContacts={participant.keyContacts} />
              </ChakraV3.Tabs.Content>
              {participant.fieldGroups.map((fieldGroup) => (
                <ParticipantProfileCardFieldGroup key={fieldGroup.id} fieldGroup={fieldGroup} />
              ))}
            </ChakraV3.Tabs.ContentGroup>
          </ChakraV3.Tabs.Root>
          <ChakraV3.Button onClick={onEditProfile} size='sm' variant='subtle'>
            <FaPencilAlt />
            Edit profile
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
